// Compact chip row showing who is assigned to a deal, plus (when the viewer
// has loans.assign) a dialog to add/remove assignments. ADR-0004 / ticket 60.
//
// Defaults to the Loan File endpoints; the Pipeline boards (ticket 69) pass a
// `client` with the same three calls pointed at an opportunity instead:
//   { list(id), add(id, {user_id, slot}), remove(id, userId, slot) }
// `lockedSlot` ({ slot, note }) marks a slot the server refuses to change (GHL owns the Loan Officer
// slot on a pre-application card): its chips lose the remove button, the slot can't be picked, and
// `note` ({ text, href, link }) shows beside the chips.
import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, X } from 'lucide-react';
import { losApi } from '../../losApi';
import { orgApi } from '../../phase567Api';
import DialogShell from '../../kit/DialogShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from '../../../hooks/use-toast';
import ManagedNote from '../../board/ManagedNote';

export const SLOT_LABELS = {
  loan_officer: 'Loan Officer',
  processor: 'Processor',
  loan_officer_assistant: 'LO Assistant',
  business_development: 'Business Development',
  underwriter: 'Underwriter',
};

const loanClient = {
  list: (id) => losApi.listAssignments(id),
  add: (id, body) => losApi.addAssignment(id, body),
  remove: (id, userId, slot) => losApi.removeAssignment(id, userId, slot),
};

export function AssignmentPicker({ loanId, canAssign, client = loanClient, lockedSlot = null, title = 'Assign to this loan', open: openProp, onOpenChange }) {
  const [assignments, setAssignments] = useState([]);
  // Uncontrolled unless the parent passes `open` (the Arive "Assign" chip opens this dialog).
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await client.list(loanId);
      setAssignments(data.assignments || []);
    } catch {
      // non-fatal — the header stays usable without assignment chips
    }
  }, [loanId, client]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    orgApi.listMembers().then((data) => setMembers(data.items || [])).catch(() => setMembers([]));
  }, [open]);

  const remove = async (userId, slot) => {
    try {
      const data = await client.remove(loanId, userId, slot);
      setAssignments(data.assignments || []);
    } catch (e) {
      toast({ title: 'Failed to remove assignment', description: e?.response?.data?.detail?.message });
    }
  };

  const save = async () => {
    if (!form.user_id || !form.slot) return;
    setSaving(true);
    try {
      const data = await client.add(loanId, { user_id: form.user_id, slot: form.slot });
      setAssignments(data.assignments || []);
      setOpen(false);
      setForm({});
    } catch (e) {
      toast({ title: 'Failed to assign', description: e?.response?.data?.detail?.message || e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="assignment-picker">
      {assignments.map((a) => {
        const display = a.name || a.email || a.user_id;
        const slotLabel = SLOT_LABELS[a.slot] || a.slot;
        return (
          <span
            key={`${a.user_id}-${a.slot}`}
            data-testid={`assignment-chip-${a.user_id}-${a.slot}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-medium text-slate-600"
          >
            {display} · {slotLabel}
            {canAssign && a.slot !== lockedSlot?.slot && (
              <button
                onClick={() => remove(a.user_id, a.slot)}
                data-testid={`assignment-remove-${a.user_id}-${a.slot}`}
                className="text-slate-400 hover:text-red-500"
                title="Remove assignment"
              >
                <X size={10} />
              </button>
            )}
          </span>
        );
      })}

      {lockedSlot && <ManagedNote note={lockedSlot.note} testId="assign-slot-managed" />}

      {canAssign && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assign-open"
          className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
          title="Assign"
        >
          <UserPlus size={13} />
        </button>
      )}

      <DialogShell
        open={open}
        onOpenChange={setOpen}
        title={title}
        size="sm"
        footer={(
          <>
            <button onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
            <button onClick={save} disabled={saving || !form.user_id || !form.slot} className="btn-gold" data-testid="assign-save">Save</button>
          </>
        )}
      >
        <div className="space-y-3">
          <div>
            <Select value={form.user_id || ''} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger data-testid="assign-member"><SelectValue placeholder="Team member" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={form.slot || ''} onValueChange={(v) => setForm({ ...form, slot: v })}>
              <SelectTrigger data-testid="assign-slot"><SelectValue placeholder="Slot" /></SelectTrigger>
              <SelectContent>
                {Object.entries(SLOT_LABELS).map(([slot, label]) => <SelectItem key={slot} value={slot} disabled={slot === lockedSlot?.slot}>{label}{slot === lockedSlot?.slot ? ' (managed in GoHighLevel)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </DialogShell>
    </div>
  );
}
